import {
  closeCurrentDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  BulkEditInvoiceDeliveryNoteRequest,
  BulkPatchDeliveryNoteRequest,
  useBulkDeleteDeliveryNoteMutation,
  useBulkEditInvoiceDeliveryNoteMutation,
  useBulkPatchDeliveryNoteMutation,
  useBulkPostReceiveNoteMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  refreshDmsDatagrid,
} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {DeliveryNoteListBulkEdit, EditMode} from './components/DeliveryNoteListBulkEdit';

export const DeliveryNoteList = () => {
  const navigate = useNavigate();

  const refreshDataGrid = () => {
    refreshDmsDatagrid(WAREHOUSE_GRID_CODES.deliveryNoteList, {deselectAll: true});
  };

  const [deleteDeliveryNote] = useBulkDeleteDeliveryNoteMutation();
  const [postReceiveNote] = useBulkPostReceiveNoteMutation();
  const [bulkPatchDeliveryNote] = useBulkPatchDeliveryNoteMutation();
  const [bulkEditInvoiceDeliveryNote] = useBulkEditInvoiceDeliveryNoteMutation();

  const handleBulkEditDeliveryNote = async (requestBody: BulkPatchDeliveryNoteRequest) => {
    await bulkPatchDeliveryNote(requestBody)
      .unwrap()
      .then(closeCurrentDialog)
      .then(refreshDataGrid)
      .catch(handleApiError);
  };

  const handleBulkEditInvoiceDeliveryNote = async (
    requestBody: BulkEditInvoiceDeliveryNoteRequest
  ) => {
    await bulkEditInvoiceDeliveryNote(requestBody)
      .unwrap()
      .then(closeCurrentDialog)
      .then(refreshDataGrid)
      .catch(handleApiError);
  };

  const handleDelete = (rowId: string | string[], deselectAll: () => void) => {
    openDeleteDialog({
      onConfirm: async () =>
        await deleteDeliveryNote({deliveryNoteId: isArray(rowId) ? rowId : [rowId]})
          .unwrap()
          .then(deselectAll)
          .then(() => {
            showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
          })
          .then(refreshDataGrid)
          .catch(handleApiError),
    });
  };

  const handleCreateReceiveNote = async (rowId: string | string[]) => {
    await postReceiveNote({deliveryNoteId: isArray(rowId) ? rowId : [rowId]})
      .unwrap()
      .then(() => showNotification.success(i18n.t('general.notifications.successfullyCreated')))
      .then(refreshDataGrid)
      .catch(handleApiError);
  };

  const actionCallback: ActionCallback = async ({
    actionKey,
    rowId,
    deselectAll,
    sourceSearchParams,
  }) => {
    const isBulk = isArray(rowId);
    await match({actionKey, isBulk})
      .with(
        {actionKey: 'redirectDetail', isBulk: false},
        {actionKey: 'edit', isBulk: false},
        () => {
          const id = isArray(rowId) ? rowId[0] : rowId;
          navigate(
            composePath(warehouseRoutes.deliveryNoteDetailOverview, {
              queryParams: sourceSearchParams,
              params: {id},
              isSearchParamsPreserved: false,
            })
          );
        }
      )
      .with({actionKey: 'edit', isBulk: true}, {actionKey: 'editInvoice'}, () => {
        const editMode: EditMode = actionKey === 'edit' ? 'full' : 'invoice';

        const handleSubmit = async (
          requestBody: BulkPatchDeliveryNoteRequest | BulkEditInvoiceDeliveryNoteRequest
        ) => {
          if (editMode === 'full') {
            return await handleBulkEditDeliveryNote(requestBody as BulkPatchDeliveryNoteRequest);
          } else {
            return await handleBulkEditInvoiceDeliveryNote(
              requestBody as BulkEditInvoiceDeliveryNoteRequest
            );
          }
        };

        openDialog(
          <DeliveryNoteListBulkEdit
            rowId={rowId}
            onSubmit={handleSubmit}
            editMode={editMode}
            data-testid={testIds.warehouse.deliveryNoteList('dialogs.bulkEdit')}
          />,
          {
            size: 'small',
            title: i18n.t('entity.warehouse.labels.bulkEdit'),
          }
        );
      })
      .with({actionKey: 'delete'}, () => {
        handleDelete(rowId, deselectAll);
      })
      .with({actionKey: 'createReceiveNote'}, async () => {
        await handleCreateReceiveNote(rowId);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper hideCardWrapper>
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.deliveryNoteList}
        actionCallback={actionCallback}
        data-testid={WAREHOUSE_GRID_CODES.deliveryNoteList}
      />
    </FullscreenDataGridWrapper>
  );
};
